import { PencilIcon, XIcon } from 'lucide-react';
import { cn } from '@rocco/utils/cn';
import { Button } from '@rocco/components/button';
import {
    Drawer,
    DrawerContent,
    DrawerDescription,
    DrawerHeader,
    DrawerTitle,
    DrawerTrigger,
} from '@rocco/components/drawer';
import { Separator } from '@rocco/components/separator';
import type { MeUser } from '~/server/types';
import { useIsSafari } from '~/modules/root';
import type { PartialConversation } from '../../types';
import {
    DRAWER_MARGIN_REM,
    DRAWER_WIDTH_REM,
    useChat,
} from '../../hooks/use-chat';
import { ParticipantAvatar } from '../avatar/ParticipantAvatar';
import { ChatInboxFloatingButton } from './ChatInboxFloatingButton';
import { ChatInboxList } from './ChatInboxList';

/**
 * The floating chat inbox bubble and the chat inbox drawer.
 */
export function ChatInboxDrawer({
    conversations,
    user,
}: {
    /**
     * The user's list of conversations. A `null` value means that the conversations
     * are not yet fetched.
     */
    conversations: PartialConversation[] | null;
    /**
     * The logged-in user.
     */
    user: MeUser;
}) {
    const isSafari = useIsSafari();
    const { chatInboxState, setChatInboxState, openChatBox } = useChat();

    return (
        <Drawer
            open={chatInboxState === 'open'}
            direction="right"
            modal={false}
            dismissible={false}
            repositionInputs={false}
            onOpenChange={(open) => setChatInboxState(open ? 'open' : 'closed')}
        >
            <DrawerTrigger asChild>
                <ChatInboxFloatingButton
                    className={cn(chatInboxState === 'open' && 'hidden')}
                />
            </DrawerTrigger>
            <DrawerContent
                className={cn(
                    // Reset the default width.
                    'data-[vaul-drawer-direction=right]:w-unset data-[vaul-drawer-direction=right]:sm:max-w-unset',
                    // Disable the default Drawer slide-in animation, implement custom animation.
                    'animate-none transition-none',
                    // Prevent the drawer content from shifting when the scrollbar is locked.
                    '[body[data-scroll-locked="1"]_&]:translate-x-[calc(0px-var(--removed-body-scroll-bar-size,15px))]',
                    // Fix for bug in Vaul for Safari where the scroll bar disappears and reappears when the drawer is opened.
                    isSafari &&
                        '[body[style*="position:_fixed"]_&]:translate-x-[calc(0px-var(--removed-body-scroll-bar-size,15px))]',
                    'bg-transparent'
                )}
                noHandle
                hideDefaultCloseButton
                style={{
                    width: `${DRAWER_WIDTH_REM}rem`,
                    marginTop: `${DRAWER_MARGIN_REM}rem`,
                    marginRight: `${DRAWER_MARGIN_REM}rem`,
                    marginBottom: `${DRAWER_MARGIN_REM}rem`,
                }}
                // Prevent the drawer from trapping focus (Radix Dialog behaviour).
                onFocus={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                }}
            >
                <div className="flex size-full flex-col rounded-xl bg-white/60 text-black backdrop-blur-xl duration-300 animate-in slide-in-from-bottom">
                    {/* Suppress missing title and description console warning */}
                    <DrawerHeader className="hidden" aria-hidden>
                        <DrawerTitle className="hidden" aria-hidden>
                            My Messages
                        </DrawerTitle>
                        <DrawerDescription className="hidden" aria-hidden />
                    </DrawerHeader>
                    {/* Header */}
                    <div className="relative flex select-text items-center gap-3 py-8 pl-4 pr-16">
                        <ParticipantAvatar
                            fallbackClassName="!text-6"
                            size="square56"
                            participant={user}
                        />
                        <div className="min-w-0 flex-1 space-y-1 leading-tight">
                            <div className="text-4 font-medium">
                                {user.Name}
                            </div>
                            <div className="truncate text-3.5 opacity-70">
                                {user.Email}
                            </div>
                        </div>
                        <button
                            className="absolute right-6 top-6 p-1"
                            aria-label="Close"
                            onClick={() => setChatInboxState('closed')}
                        >
                            <XIcon className="size-6 opacity-70 hover:opacity-100" />
                        </button>
                    </div>
                    {/* Inbox list */}
                    <ChatInboxList
                        className="min-h-0 flex-1 px-4 py-2"
                        conversations={conversations ?? []}
                        isLoading={!conversations}
                    />
                    <div className="px-4">
                        <Separator
                            className="bg-white"
                            orientation="horizontal"
                        />
                    </div>
                    {/* New message button */}
                    <div className="flex items-center justify-center px-2 py-8">
                        {/* Make sure the new message button lines up with the chat box send button. */}
                        <Button
                            className="flex items-center gap-2 bg-black/70 py-2 hover:bg-black"
                            color="dark"
                            size="larger"
                            onClick={() => openChatBox(0)}
                        >
                            New Message
                            <PencilIcon className="size-4" />
                        </Button>
                    </div>
                </div>
            </DrawerContent>
        </Drawer>
    );
}
